import React from 'react';
import ReactDOM from 'react-dom';

interface AIProcessingPopupProps {
  show: boolean;
  title?: string;
  description?: string;
  subDescription?: string;
}

/**
 * AI処理中ポップアップコンポーネント
 *
 * AI消しゴム、AI青空加工、AI自動モザイクなど、
 * AI処理の実行中に表示する共通ポップアップです。
 *
 * 使用例：
 * ```tsx
 * <AIProcessingPopup
 *   show={isProcessing}
 *   title="AI青空加工を実行中..."
 *   description="AIが空を検出して青空に変換しています。"
 *   subDescription="しばらくお待ちください..."
 * />
 * ```
 */
export const AIProcessingPopup: React.FC<AIProcessingPopupProps> = ({
  show,
  title = 'AI処理中...',
  description = 'AIが処理を実行しています。',
  subDescription = 'しばらくお待ちください...'
}) => {
  if (!show) return null;

  const popupContent = (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }}>
      <div className="fixed inset-0 bg-black bg-opacity-50" style={{ zIndex: 99999 }}></div>
      <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" style={{ zIndex: 100000 }}>
        <h3 className="text-lg font-semibold mb-4">{title}</h3>

        <div className="flex flex-col items-center">
          {/* ローディングアニメーション */}
          <div className="relative mb-6 w-16 h-16">
            {/* 外側の円 */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                border: '4px solid #e5e7eb',
                animation: 'spin 2s linear infinite',
              }}
            ></div>
            {/* 内側の円（グラデーション） */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                borderTop: '4px solid #f97316',
                borderRight: '4px solid #fb923c',
                borderBottom: '4px solid transparent',
                borderLeft: '4px solid transparent',
                animation: 'spin 1s linear infinite',
              }}
            ></div>
          </div>

          {/* プログレスバー風の表示 */}
          <div className="w-full max-w-xs mb-4">
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"
                style={{
                  animation: 'aiProcessingProgress 2s ease-in-out infinite',
                }}
              ></div>
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center">
            {description}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {subDescription}
          </p>
        </div>

        {/* カスタムスタイル */}
        <style>{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          @keyframes aiProcessingProgress {
            0% {
              width: 0%;
              margin-left: 0;
            }
            50% {
              width: 60%;
              margin-left: 0;
            }
            100% {
              width: 100%;
              margin-left: 0;
            }
          }
        `}</style>
      </div>
    </div>
  );

  // React Portalを使用してbodyの直下にレンダリング
  return ReactDOM.createPortal(popupContent, document.body);
};
